import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import { useCreateQuoteDraft } from '@/hooks/useQuotes';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { FormPage, FormSection } from '@/components/ui/FormPage';
import { PageLayout } from '@/components/ui/PageLayout';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { TYPE, UI_MUTED, UI_SECONDARY } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function NewQuote() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');

  const picker = usePatientPicker(activeClinicId, search);
  const create = useCreateQuoteDraft();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!activeClinicId) return;
    if (!selectedPatient) {
      toast.error(t('quotes.patient_required'));
      return;
    }
    try {
      const quote = await create.mutateAsync({
        clinic_id: activeClinicId,
        patient_id: selectedPatient.id,
        valid_until: validUntil || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('quotes.toast_created'));
      navigate(`/quotes/${quote.id}`);
    } catch (err) {
      console.error('create quote draft failed', err);
      toast.error(t('quotes.save_error'));
    }
  }

  const patientName = selectedPatient
    ? `${selectedPatient.first_name} ${selectedPatient.last_name}`.trim()
    : '';

  return (
    <PageLayout>
    <form onSubmit={onSubmit}>
      <FormPage
        breadcrumb={[
          { label: t('quotes.back_to_list'), to: '/economy/quotes' },
          { label: t('quotes.new_quote') },
        ]}
        title={t('quotes.new_quote')}
        onCancel={() => navigate('/economy/quotes')}
        primary={
          <Button
            type="submit"
            loading={create.isPending}
            loadingText={t('common.saving')}
            disabled={!selectedPatient}
          >
            {t('quotes.save_draft')}
          </Button>
        }
      >
        <FormSection title={t('quotes.patient_picker_label')}>
          {selectedPatient ? (
            <div className="flex items-center gap-s3 rounded-pop bg-ds-sunk p-s3">
              <Avatar name={patientName} size={36} />
              <div className="min-w-0 flex-1">
                <div className={cn(TYPE.section, 'text-ds-text')}>{patientName}</div>
                {formatPatientTelephoneNumbers(
                  selectedPatient.telephone_subscribers,
                  selectedPatient.phone,
                ) ? (
                  <div className={UI_MUTED}>
                    {formatPatientTelephoneNumbers(
                      selectedPatient.telephone_subscribers,
                      selectedPatient.phone,
                    )}
                  </div>
                ) : null}
              </div>
              <Button type="button" variant="tertiary" size="sm" onClick={() => setSelectedPatient(null)}>
                {t('common.edit')}
              </Button>
            </div>
          ) : (
            <div>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('quotes.patient_picker_placeholder')}
                leadingIcon={<Search className="h-4 w-4" />}
              />
              {picker.isLoading ? (
                <div className={cn('mt-s2 flex items-center gap-s2', UI_SECONDARY)}>
                  <Loader2 className="h-3 w-3 animate-spin" /> {t('common.loading')}
                </div>
              ) : null}
              {picker.data && picker.data.length > 0 ? (
                <div className="mt-s2 max-h-60 overflow-y-auto rounded-pop border border-ds-line bg-ds-card shadow-pop">
                  {picker.data.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSelectedPatient(p);
                        setSearch('');
                      }}
                      className={cn(
                        'flex w-full flex-col items-start border-b border-ds-line px-s3 py-s2 text-left last:border-b-0 hover:bg-ds-sunk',
                        TYPE.body,
                      )}
                    >
                      <span className="text-ds-text">
                        {p.first_name} {p.last_name}
                      </span>
                      {formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone) ? (
                        <span className={UI_SECONDARY}>
                          {formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone)}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </FormSection>

        <FormSection title={t('quotes.field_valid_until')}>
          <Input
            label={t('quotes.field_valid_until')}
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </FormSection>

        <FormSection title={t('quotes.field_notes')}>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </FormSection>
      </FormPage>
    </form>
    </PageLayout>
  );
}
